import supabase from '@/lib/supabase';
import type {BackofficeUser} from './types';

/**
 * Recupera i dettagli dell'utente backoffice dal DB
 * @param userId - L'ID dell'utente autenticato (auth.users.id)
 * @param timeoutMs - Timeout in millisecondi (default: 8000)
 * @returns I dettagli dell'utente backoffice o null se non trovato
 */
export const getBackofficeUser = async (
  userId: string,
  timeoutMs = 8000,
): Promise<BackofficeUser | null> => {
  try {
    const result = await Promise.race([
      // Query a Supabase
      supabase
        .from('backoffice_users')
        .select('*')
        .eq('id', userId)
        .single(),
      // Timeout
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Timeout nel recupero utente backoffice')), timeoutMs)
      ),
    ]);

    const {data, error} = result;

    if (error) {
      // Se l'errore è "PGRST116" significa che il record non esiste
      if (error.code === 'PGRST116') {
        console.warn(`Utente backoffice non trovato per userId: ${userId}`);
        return null;
      }
      console.error('Errore nel recupero utente backoffice:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Errore nel recupero utente backoffice:', error);
    return null;
  }
};

/**
 * Verifica se l'utente corrente è un amministratore
 * @param userId - L'ID dell'utente autenticato
 * @returns true se l'utente è admin, false altrimenti
 */
export const isBackofficeAdmin = async (
  userId: string,
): Promise<boolean> => {
  const backofficeUser = await getBackofficeUser(userId);
  return backofficeUser?.is_admin ?? false;
};

/**
 * Verifica se l'utente corrente è attivo
 * @param userId - L'ID dell'utente autenticato
 * @returns true se l'utente è attivo, false altrimenti
 */
export const isBackofficeUserActive = async (
  userId: string,
): Promise<boolean> => {
  const backofficeUser = await getBackofficeUser(userId);
  return backofficeUser?.status === 'active';
};
